import { useState } from "react";
import { useParams, Link } from "wouter";
import { toast } from "sonner";
import {
  useGetTask,
  useListUsers,
  getListUsersQueryKey,
  useGetAccount,
  getGetAccountQueryKey,
  useAssignTask,
  useReassignTask,
  useStartTask,
  useHoldTask,
  useResumeTask,
  useResolveTask,
  useCancelTask,
  useOverrideTaskPriority,
} from "@workspace/api-client-react";
import type {
  TaskActivity,
  TaskCancelReason,
  TaskChecklistItem,
  TaskPriority,
  TaskStatus,
  User,
} from "@workspace/api-client-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { memId } from "@/lib/display-id";

// Phase 41 Plan 09 — task-detail: append-only activity log (TASK-05/06),
// status transitions ONLY through the generated transition hooks (never a
// hand-rolled status write — transitionTaskStatusTx is the server's sole
// writer, TASK-03), Reassign (TPRM-04/SC5 — works from assigned AND
// in_progress, server-enforced 403 surfaced verbatim) and priority-override
// (mandatory >=10-char note, server-enforced 422 surfaced verbatim). The
// task is NEVER deletable — no delete/edit affordance exists anywhere on
// this page, by construction.

const CANCEL_REASONS: { value: TaskCancelReason; label: string }[] = [
  { value: "duplicate", label: "Duplicate" },
  { value: "member_withdrew", label: "Member withdrew" },
  { value: "not_reproducible", label: "Not reproducible" },
  { value: "out_of_scope", label: "Out of scope" },
  { value: "other", label: "Other" },
];

const PRIORITIES: TaskPriority[] = ["P1", "P2", "P3", "P4"];

// Every role the DB CHECK allows (41-CONTEXT.md "Roles, hierarchy &
// permissions") — the server (canReassign) is the sole authority on which
// of these a given actor may actually target; a UI-side subset would be a
// UI-only hide, which the plan explicitly forbids.
const ALL_ROLES = [
  "super_admin",
  "admin",
  "centralised_team",
  "agent",
  "ops_lead",
  "front_desk",
  "housekeeping",
  "bde",
] as const;

function fmtTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.round(day / 30);
  if (month < 12) return `${month}mo ago`;
  return `${Math.round(month / 12)}y ago`;
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "cancelled") return "destructive";
  if (status === "resolved" || status === "closed") return "secondary";
  if (status === "on_hold") return "outline";
  return "default";
}

/** Reads the shape customFetch's ApiError throws — .status + parsed .data (never assume a field exists beyond ErrorResponse's declared {error}). */
function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
/** Phase 42 Plan 15 (TSLA-06/07/08/09) — the SLA sweep (42-12) writes a
 *  person-targeted escalation recipient as the token "user:<id>"; anything
 *  else is a plain role string straight out of `notify_role`. Resolves the
 *  former to a real name via `userName` so a recipient list never reads as
 *  a raw DB reference. */
function formatRecipient(raw: string, userName: (uid: number | null | undefined) => string): string {
  const match = /^user:(\d+)$/.exec(raw);
  if (match) return userName(Number(match[1]));
  return raw.replace(/_/g, " ");
}

/** `checklist_reject`'s pinned metadata (42-11/42-12) carries only
 *  `{ itemId, rejectReason }` — no `itemLabel`. Resolve the label off the
 *  task's own `checklistItems` (TaskDetail) so the activity row can still
 *  name WHICH item was rejected. */
function checklistItemLabel(items: TaskChecklistItem[] | undefined, itemId: unknown): string | undefined {
  if (typeof itemId !== "number") return undefined;
  return items?.find((it) => it.id === itemId)?.label;
}

function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}

export default function TaskDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const { data: task, isLoading, refetch } = useGetTask(id);
  const { data: users, isError: usersUnavailable } = useListUsers({
    query: { retry: false, queryKey: getListUsersQueryKey() },
  });
  const { data: account } = useGetAccount(task?.accountId ?? 0, {
    query: { enabled: !!task?.accountId, queryKey: getGetAccountQueryKey(task?.accountId ?? 0) },
  });

  const assignMutation = useAssignTask();
  const reassignMutation = useReassignTask();
  const startMutation = useStartTask();
  const holdMutation = useHoldTask();
  const resumeMutation = useResumeTask();
  const resolveMutation = useResolveTask();
  const cancelMutation = useCancelTask();
  const priorityMutation = useOverrideTaskPriority();

  // Assign (open->assigned) and Reassign (assigned/in_progress, no status edge)
  // share one dialog shape — same fields, different hook underneath.
  const [assignDialog, setAssignDialog] = useState<null | "assign" | "reassign">(null);
  const [pickedAssigneeId, setPickedAssigneeId] = useState<string>("");
  const [pickedRole, setPickedRole] = useState<string>("");

  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<TaskCancelReason | "">("");
  const [cancelNote, setCancelNote] = useState("");

  const [priorityOpen, setPriorityOpen] = useState(false);
  const [newPriority, setNewPriority] = useState<TaskPriority>("P3");
  const [priorityNote, setPriorityNote] = useState("");

  function userName(uid: number | null | undefined): string {
    if (uid == null) return "—";
    return users?.find((u) => u.id === uid)?.name ?? `User #${uid}`;
  }

  /** Shared 409/422/403 -> toast mapping (TASK-11's attributed conflict message + auto-refresh). */
  function handleMutationError(err: unknown, fallback: string) {
    const status = errStatus(err);
    const data = errData(err);
    if (status === 409) {
      // TASK-11: "Already {from} by {name} at {time}" — server-composed, shown verbatim.
      toast.error((data?.message as string) ?? "Someone else already changed this task.");
      refetch();
      return;
    }
    if (status === 422 && data?.error === "illegal_transition") {
      const legal = (data.legal as string[] | undefined) ?? [];
      toast.error(`Can't move from "${data.from}" to "${data.to}".`, {
        description: legal.length ? `Legal next steps: ${legal.join(", ")}` : "No further transitions from here.",
      });
      refetch();
      return;
    }
    if (status === 403) {
      toast.error((data?.message as string) ?? "You don't have permission to do that.");
      return;
    }
    if (status === 422) {
      toast.error((data?.message as string) ?? fallback);
      return;
    }
    toast.error((data?.message as string) ?? (data?.error as string) ?? fallback);
  }

  function openAssign(mode: "assign" | "reassign") {
    setPickedAssigneeId(task?.assigneeId != null ? String(task.assigneeId) : "");
    setPickedRole(task?.assignedRole ?? "");
    setAssignDialog(mode);
  }

  async function submitAssign() {
    if (!task) return;
    const data = {
      assigneeId: pickedAssigneeId ? Number(pickedAssigneeId) : null,
      assignedRole: pickedRole || null,
    };
    try {
      if (assignDialog === "assign") {
        await assignMutation.mutateAsync({ id: task.id, data });
        toast.success("Task assigned.");
      } else {
        await reassignMutation.mutateAsync({ id: task.id, data });
        toast.success("Task reassigned.");
      }
      setAssignDialog(null);
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not update the assignee.");
    }
  }

  async function doStart() {
    if (!task) return;
    try {
      await startMutation.mutateAsync({ id: task.id });
      toast.success("Task started.");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not start the task.");
    }
  }

  async function submitHold() {
    if (!task) return;
    if (holdReason.trim().length === 0) {
      toast.error("A hold reason is required.");
      return;
    }
    try {
      await holdMutation.mutateAsync({ id: task.id, data: { holdReason: holdReason.trim() } });
      toast.success("Task put on hold.");
      setHoldOpen(false);
      setHoldReason("");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not put the task on hold.");
    }
  }

  async function doResume() {
    if (!task) return;
    try {
      await resumeMutation.mutateAsync({ id: task.id });
      toast.success("Task resumed.");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not resume the task.");
    }
  }

  async function doResolve() {
    if (!task) return;
    try {
      await resolveMutation.mutateAsync({ id: task.id });
      toast.success("Task resolved.");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not resolve the task.");
    }
  }

  async function submitCancel() {
    if (!task) return;
    if (!cancelReason) {
      toast.error("Pick a cancel reason.");
      return;
    }
    if (cancelNote.trim().length < 10) {
      toast.error("Cancel note must be at least 10 characters.");
      return;
    }
    try {
      await cancelMutation.mutateAsync({
        id: task.id,
        data: { cancelReason, cancelNote: cancelNote.trim() },
      });
      toast.success("Task cancelled.");
      setCancelOpen(false);
      setCancelReason("");
      setCancelNote("");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not cancel the task.");
    }
  }

  async function submitPriority() {
    if (!task) return;
    if (priorityNote.trim().length < 10) {
      toast.error("A note of at least 10 characters is required.");
      return;
    }
    try {
      await priorityMutation.mutateAsync({
        id: task.id,
        data: { priority: newPriority, note: priorityNote.trim() },
      });
      toast.success("Priority updated.");
      setPriorityOpen(false);
      setPriorityNote("");
      refetch();
    } catch (err) {
      handleMutationError(err, "Could not override priority.");
    }
  }

  function describeActivity(a: TaskActivity): string {
    const m = (a.metadata ?? {}) as Record<string, unknown>;
    const who =
      a.actor === "user" ? userName(a.actorUserId) : a.actor === "priya" ? "Priya (bot)" : a.actor === "riya" ? "Riya (bot)" : "System";
    switch (a.eventType) {
      case "created":
        return `${who} created this ${m.type ?? ""} task via ${m.channel ?? "?"} — "${m.title ?? ""}"`;
      case "status_change": {
        let extra = "";
        if (m.holdReason) extra = ` — "${m.holdReason}"`;
        else if (m.cancelReason) extra = ` — ${m.cancelReason}: "${m.cancelNote}"`;
        else if (m.to === "assigned" && (m.assigneeId || m.assignedRole)) {
          extra = ` — to ${m.assigneeId ? userName(m.assigneeId as number) : `role ${m.assignedRole}`}`;
        }
        return `${who} moved ${m.from ?? "—"} → ${m.to}${extra}`;
      }
      case "assignment": {
        const target = m.toUser ? userName(m.toUser as number) : m.toRole ? `role ${m.toRole}` : "unassigned";
        return `${who} reassigned to ${target}`;
      }
      case "priority_override":
        return `${who} changed priority ${m.from} → ${m.to} — "${m.note}"`;
      case "cancelled":
        return `${who} cancelled — ${m.cancelReason ?? ""}: "${m.cancelNote ?? ""}"`;
      // Phase 42 Plan 15 (TSLA-06/07/08/09, TPRF-04) — this phase ships no
      // popup/unread-count surface anywhere (Phase 50 owns actually paging
      // someone); this activity log is the ONLY place a human reads the
      // escalation ladder 42-12 records. Every arm below reads 42-12's
      // pinned metadata contract verbatim and ALWAYS names the `subject`
      // (doer/validator) explicitly — a breach row that doesn't say WHO is
      // late re-creates the exact ambiguity the dual clock exists to
      // remove.
      case "sla_nudge": {
        const subject = m.subject === "validator" ? "validator" : "doer";
        const itemLabel =
          (m.itemLabel as string | undefined) ?? checklistItemLabel(task?.checklistItems, m.itemId) ?? "this item";
        const at = m.occurrenceAtTime ? ` (${m.occurrenceAtTime as string})` : "";
        return `Nudge: ${subject} halfway through the window — ${itemLabel}${at}`;
      }
      case "sla_breach": {
        const subject = m.subject === "validator" ? "validator" : "doer";
        const itemLabel =
          (m.itemLabel as string | undefined) ?? checklistItemLabel(task?.checklistItems, m.itemId) ?? "this item";
        const at = m.occurrenceAtTime ? ` (${m.occurrenceAtTime as string})` : "";
        return `SLA breach: ${subject} missed the window — ${itemLabel}${at}`;
      }
      case "escalation": {
        const recipients = Array.isArray(m.recipients)
          ? (m.recipients as unknown[]).map((r) => formatRecipient(String(r), userName)).join(", ")
          : "no one configured";
        const subject = m.subject === "validator" ? "validator" : "doer";
        const itemLabel = (m.itemLabel as string | undefined) ?? checklistItemLabel(task?.checklistItems, m.itemId);
        // Task-level rungs (p1_created/sla_50/sla_breach/p1_breach) carry no
        // checklist item at all — degrade to the rung name rather than
        // printing "undefined" or implying a doer/validator attribution
        // that doesn't apply at the task level.
        const reason = itemLabel
          ? `${subject} breach on ${itemLabel}`
          : `task-level ${((m.triggerType as string | undefined) ?? "sla").replace(/_/g, " ")} rung`;
        return `Escalated to ${recipients} — ${reason}`;
      }
      case "checklist_reject": {
        const itemLabel = checklistItemLabel(task?.checklistItems, m.itemId) ?? "this item";
        return `${who} rejected ${itemLabel} — "${(m.rejectReason as string | undefined) ?? ""}"`;
      }
      default:
        return `${who} — ${a.eventType}`;
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }

  if (!task) {
    return <div className="flex items-center justify-center h-48 text-muted-foreground">Task not found.</div>;
  }

  const status = task.status as TaskStatus;
  const canAssign = status === "open";
  const canStart = status === "assigned";
  const canReassignNow = status === "assigned" || status === "in_progress";
  const canHold = status === "in_progress";
  const canResume = status === "on_hold";
  const canResolveNow = status === "in_progress";
  // TASK-03 status machine (lib/tasks/src/status-machine.ts): cancel is legal only
  // from open/assigned/in_progress — NOT from on_hold, resolved, closed, cancelled.
  const canCancelNow = status === "open" || status === "assigned" || status === "in_progress";

  const sortedActivity = [...(task.activity ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <h1 className="text-2xl font-semibold font-mono">{task.taskNumber}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="capitalize">{task.type.replace(/_/g, " ")}</Badge>
            <Badge variant={statusVariant(status)} className="capitalize">{status.replace(/_/g, " ")}</Badge>
            <Badge variant="secondary">{task.priority}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAssign && (
            <Button size="sm" onClick={() => openAssign("assign")} disabled={assignMutation.isPending}>
              Assign
            </Button>
          )}
          {canStart && (
            <Button size="sm" onClick={doStart} disabled={startMutation.isPending}>
              Start
            </Button>
          )}
          {canReassignNow && (
            <Button size="sm" variant="outline" onClick={() => openAssign("reassign")}>
              Reassign
            </Button>
          )}
          {canHold && (
            <Button size="sm" variant="outline" onClick={() => setHoldOpen(true)}>
              Hold
            </Button>
          )}
          {canResume && (
            <Button size="sm" onClick={doResume} disabled={resumeMutation.isPending}>
              Resume
            </Button>
          )}
          {canResolveNow && (
            <Button size="sm" onClick={doResolve} disabled={resolveMutation.isPending}>
              Resolve
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => { setNewPriority(task.priority); setPriorityOpen(true); }}>
            Override priority
          </Button>
          {canCancelNow && (
            <Button size="sm" variant="destructive" onClick={() => setCancelOpen(true)}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Title</div>
              <div className="font-medium">{task.title}</div>
            </div>
            {task.description && (
              <div>
                <div className="text-xs text-muted-foreground">Description</div>
                <div>{task.description}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground">Zone</div>
              <div>{task.zoneLabel}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Account</div>
              <div>
                {account ? (
                  <Link href={`/accounts/${account.id}`} className="text-primary hover:underline">
                    {account.legalName} <span className="font-mono text-xs text-muted-foreground">{memId(account.id)}</span>
                  </Link>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Assignee</div>
              <div>
                {task.assigneeId != null
                  ? userName(task.assigneeId)
                  : task.assignedRole
                    ? `Role pool: ${task.assignedRole.replace(/_/g, " ")}`
                    : "Unassigned"}
              </div>
            </div>
            {task.vendorName && (
              <div>
                <div className="text-xs text-muted-foreground">Vendor</div>
                <div>{task.vendorName}{task.vendorExpectedAt ? ` — expected ${fmtTimestamp(task.vendorExpectedAt)}` : ""}</div>
              </div>
            )}
            {task.holdReason && (
              <div>
                <div className="text-xs text-muted-foreground">Hold reason</div>
                <div>{task.holdReason}</div>
              </div>
            )}
            {task.cancelReason && (
              <div>
                <div className="text-xs text-muted-foreground">Cancel reason</div>
                <div>{task.cancelReason.replace(/_/g, " ")} — {task.cancelNote}</div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[60vh] pr-4">
              {sortedActivity.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No activity yet.</div>
              ) : (
                <ol className="relative border-l-2 border-muted ml-3 space-y-4 py-2">
                  {sortedActivity.map((a) => (
                    <li key={a.id} className="ml-5">
                      <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                      <div className="text-sm">{describeActivity(a)}</div>
                      <div className="text-xs text-muted-foreground">
                        {relativeTime(a.createdAt)} · {fmtTimestamp(a.createdAt)}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Assign / Reassign — same dialog shape (target role and/or assignee) */}
      <Dialog open={assignDialog !== null} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{assignDialog === "assign" ? "Assign task" : "Reassign task"}</DialogTitle>
            <DialogDescription>
              Pick a specific team member and/or a role pool. The server enforces who may be
              targeted — a rejected role or person will show as an error here, not silently hide.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Assignee {usersUnavailable && <span className="text-xs text-muted-foreground">(list needs an admin login — use role pool instead)</span>}</Label>
              <Select
                value={pickedAssigneeId || "__none__"}
                onValueChange={(v) => setPickedAssigneeId(v === "__none__" ? "" : v)}
                disabled={usersUnavailable}
              >
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {(users ?? []).map((u: User) => (
                    <SelectItem key={u.id} value={String(u.id)}>{u.name} · {u.role.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target role (pool)</Label>
              <Select value={pickedRole || "__none__"} onValueChange={(v) => setPickedRole(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="No role pool" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No role pool</SelectItem>
                  {ALL_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button onClick={submitAssign} disabled={assignMutation.isPending || reassignMutation.isPending}>
              {assignDialog === "assign" ? "Assign" : "Reassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold — TASK-09 mandatory reason */}
      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Put task on hold</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Hold reason <span className="text-destructive">*</span></Label>
            <Textarea value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="Why is this on hold?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldOpen(false)}>Discard</Button>
            <Button onClick={submitHold} disabled={holdMutation.isPending}>Put on hold</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel — TASK-07 mandatory enum reason + note >= 10 chars */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel task</DialogTitle>
            <DialogDescription>The task is never deleted — cancellation is recorded in the activity log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Select value={cancelReason} onValueChange={(v) => setCancelReason(v as TaskCancelReason)}>
                <SelectTrigger><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note <span className="text-destructive">*</span> (min 10 characters)</Label>
              <Textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Explain the cancellation…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Discard</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={cancelMutation.isPending}>
              Cancel task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Priority override — mandatory >= 10-char note, server 422/403 authoritative */}
      <Dialog open={priorityOpen} onOpenChange={setPriorityOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override priority</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>New priority</Label>
              <Select value={newPriority} onValueChange={(v) => setNewPriority(v as TaskPriority)}>
                <SelectTrigger className="max-w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note <span className="text-destructive">*</span> (min 10 characters)</Label>
              <Textarea value={priorityNote} onChange={(e) => setPriorityNote(e.target.value)} placeholder="Why does the priority change?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorityOpen(false)}>Discard</Button>
            <Button onClick={submitPriority} disabled={priorityMutation.isPending}>Save priority</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
